import api from './api';

export interface BillingItem {
  id: string;
  todoId?: string;
  pharmacyId: string;
  patientCompositeId?: string;
  patientId?: string;
  patientName?: string;
  medicationName?: string;
  medications?: any[];
  rxNumber?: string;
  status: 'pending' | 'processing' | 'approved' | 'converted' | 'cancelled' | 'on_hold';
  subscriptionStatus?: 'active' | 'paused' | 'cancelled' | 'scheduled';
  autoRefillEnabled?: boolean;
  priority: 'urgent' | 'high' | 'on_time' | 'low';
  dueDate?: Date | string;
  nextFollowUpDate?: Date | string;
  daysSupply?: number;
  copayAmount?: number;
  billingStatus?: string;
  rescheduledDate?: Date | string;
  rescheduledBy?: string;
  rescheduledAt?: Date | string;
  teamNotes?: Array<{
    note: string;
    addedBy: string;
    addedAt: Date | string;
  }>;
  selectedForBatch?: boolean;
  convertedToOrderId?: string;
  orderNumber?: string;
  convertedAt?: Date | string;
  lastModifiedBy?: string;
  lastModifiedAt?: Date | string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  // Delivery frequency field (from patient preferences cycleLength: 14, 28, 56, 84)
  deliveryFrequency?: string;
  metadata?: any;
  // Enriched fields from backend
  lastDeliveryDate?: Date | string;
  currentOrderStage?: string;
  cancelledAt?: Date | string;
  cancelledReason?: string;
  // Troubleshooting fields
  inTroubleshooting?: boolean;
  troubleshootingReason?: string;
  troubleshootingNotes?: string;
  troubleshootingStartedAt?: Date | string;
}

export interface GetScheduledItemsParams {
  pharmacyId: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  priority?: string;
  patientId?: string;
  subscriptionStatus?: string;
  excludeStatus?: string;
  includeTroubleshooting?: boolean;
  search?: string;
  // Pagination
  page?: number;
  limit?: number;
  // Sorting
  sortBy?: 'followUpDate' | 'patientName' | 'copay' | 'nextFollowUpDate' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

class SubscriptionService {
  // ============ Billing Item Management Methods ============

  /**
   * Get scheduled billing items (pending subscriptions)
   */
  async getScheduledItems(params: GetScheduledItemsParams): Promise<{
    success: boolean;
    count: number;
    total?: number;
    page?: number;
    limit?: number;
    data: BillingItem[];
  }> {
    const { pharmacyId, ...restParams } = params;
    const queryParams = new URLSearchParams();

    Object.entries(restParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const url = `/api/billing-queue/${pharmacyId}/items${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data;
  }

  /**
   * Reschedule a billing item's follow-up date
   */
  async rescheduleItem(
    itemId: string,
    newDate: string,
    reason?: string,
  ): Promise<{
    success: boolean;
    message: string;
    data: BillingItem;
  }> {
    const response = await api.patch(`/api/billing-queue/items/${itemId}`, {
      nextFollowUpDate: newDate,
      notes: reason,
    });
    return response.data;
  }

  /**
   * Bulk reschedule multiple billing items
   */
  async bulkRescheduleItems(
    itemIds: string[],
    newDate: string,
    reason?: string,
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      count: number;
      itemIds: string[];
    };
  }> {
    const response = await api.patch('/billing/items/bulk-reschedule', {
      itemIds,
      newDate,
      reason,
    });
    return response.data;
  }

  /**
   * Pause a billing item subscription
   */
  async pauseItem(
    itemId: string,
    reason?: string,
  ): Promise<{
    success: boolean;
    message: string;
    data: BillingItem;
  }> {
    const response = await api.patch(`/api/billing-queue/items/${itemId}`, {
      subscriptionStatus: 'paused',
      notes: reason,
    });
    return response.data;
  }

  /**
   * Resume a paused billing item subscription
   */
  async resumeItem(
    itemId: string,
    nextDate?: string,
  ): Promise<{
    success: boolean;
    message: string;
    data: BillingItem;
  }> {
    const updateData: any = {
      subscriptionStatus: 'active',
    };
    if (nextDate) {
      updateData.nextFollowUpDate = nextDate;
    }
    const response = await api.patch(`/api/billing-queue/items/${itemId}`, updateData);
    return response.data;
  }

  /**
   * Cancel a billing item subscription
   */
  async cancelItem(
    itemId: string,
    reason: string,
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      id: string;
      cancelledAt: Date | string;
      reason: string;
    };
  }> {
    const response = await api.patch(`/api/billing-queue/items/${itemId}`, {
      subscriptionStatus: 'cancelled',
      notes: reason,
    });
    return response.data;
  }

  /**
   * Convert billing item to order
   *
   * ⚠️ NOTE: This method is NOT used directly for conversion.
   * Use ProcessBillingModal component which handles the full conversion workflow.
   * This endpoint requires a complex DTO with:
   * - medicationDetailsList (from Kroll query)
   * - paymentInfo (payment details)
   * - deliverySettings (address, date, contact method)
   * - orderType ('delivery' or 'pickup')
   *
   * See: frontend/src/components/billing/ProcessBillingModal.tsx for correct usage
   * See: backend-v2/src/v2/orders/dto/convert-from-billing.dto.ts for DTO structure
   */
  async convertItemToOrder(itemId: string, conversionData: any): Promise<{
    success: boolean;
    order: {
      id: string;
      orderNumber: string;
      workflowStage: string;
      status: string;
      priority: string;
      patientId: string;
      patientName: string;
      pharmacyId: string;
      createdAt: Date;
      createdBy: string;
    };
    message: string;
  }> {
    // Uses the v2 orders endpoint for conversion
    const response = await api.post(`/api/v2/orders/convert-from-billing/${itemId}`, conversionData);
    return response.data;
  }

  /**
   * Batch convert billing items to orders
   *
   * ⚠️ NOTE: Backend batch convert endpoint does NOT exist yet.
   * This is a placeholder for future implementation.
   * Current workaround: Call convertItemToOrder() multiple times or use ProcessBillingModal for each item.
   */
  async batchConvertItems(billingItemIds: string[], conversionData: any): Promise<{
    success: boolean;
    message: string;
    data: {
      converted: number;
      total: number;
      failed: number;
      orders: Array<{
        orderId: string;
        orderNumber: string;
        billingItemId: string;
      }>;
      errors: Array<{
        billingItemId: string;
        error: string;
      }>;
    };
  }> {
    // Temporary implementation: Convert items sequentially
    // TODO: Create backend endpoint for true batch conversion
    const results = {
      converted: 0,
      total: billingItemIds.length,
      failed: 0,
      orders: [] as Array<{ orderId: string; orderNumber: string; billingItemId: string }>,
      errors: [] as Array<{ billingItemId: string; error: string }>,
    };

    for (const itemId of billingItemIds) {
      try {
        const response = await this.convertItemToOrder(itemId, conversionData);
        results.converted++;
        results.orders.push({
          orderId: response.order.id,
          orderNumber: response.order.orderNumber,
          billingItemId: itemId,
        });
      } catch (error: any) {
        results.failed++;
        results.errors.push({
          billingItemId: itemId,
          error: error.response?.data?.message || error.message || 'Conversion failed',
        });
      }
    }

    return {
      success: results.failed === 0,
      message: `Converted ${results.converted} of ${results.total} items`,
      data: results,
    };
  }

  /**
   * Cascade cancel subscription and active order
   *
   * This comprehensive cancellation:
   * - Cancels the subscription
   * - Adds active order to "To Be Cancelled" queue for Kroll reversal
   * - Handles non-cancellable orders gracefully
   * - Returns detailed response with warnings
   */
  async cascadeCancelSubscription(
    pharmacyId: string,
    subscriptionId: string,
    reason: string,
    notes?: string,
    cancelledBy?: string,
  ): Promise<{
    success: boolean;
    message: string;
    subscription: any;
    order?: any;
    warnings?: string[];
  }> {
    // "Cancel Subscription" also cancels the current order (product 2026-06-17):
    // hard-cancels a cancellable in-flight order + the subscription in one step,
    // or cancels the subscription only when the order is already shipped / absent.
    // cancelledBy is taken from the auth token server-side.
    const response = await api.post(
      `/api/orders/${pharmacyId}/subscriptions/${subscriptionId}/cancel-with-order`,
      {
        cancellationReason: reason,
        cancellationNotes: notes,
      }
    );
    return response.data;
  }

  /**
   * Set/clear the permanent PRN classifier on a single basket medication.
   * The backend repositions the basket's next_follow_up_date in the same
   * round-trip (a PRN drug no longer drives the refill cycle).
   */
  async setMedicationPrn(billingItemId: string, rxNumber: string, isPrn: boolean): Promise<unknown> {
    const response = await api.patch(
      `/api/billing-queue/items/${encodeURIComponent(billingItemId)}/medications/${encodeURIComponent(rxNumber)}/prn`,
      { isPrn },
    );
    return response.data;
  }

  // ============ Subscription Details Methods ============

  /**
   * Get a single billing item by ID
   * Used by SubscriptionDetailsDrawer to fetch subscription information
   */
  async getBillingItemById(itemId: string): Promise<BillingItem> {
    const response = await api.get(`/api/billing-queue/items/${itemId}`);
    return response.data;
  }

  /**
   * Get all orders for a subscription with pagination
   * Used by SubscriptionOrdersTab to display order history
   */
  async getSubscriptionOrders(
    subscriptionId: string,
    query?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'ASC' | 'DESC';
    }
  ): Promise<{
    success: boolean;
    data: any[];
    total: number;
    page: number;
    limit: number;
  }> {
    const params = new URLSearchParams();
    if (query?.page) params.append('page', query.page.toString());
    if (query?.limit) params.append('limit', query.limit.toString());
    if (query?.sortBy) params.append('sortBy', query.sortBy);
    if (query?.sortOrder) params.append('sortOrder', query.sortOrder);

    const queryString = params.toString();
    const url = `/api/billing-queue/items/${subscriptionId}/orders${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data;
  }

  /**
   * Get complete history timeline for a subscription
   * Used by SubscriptionHistoryTab to display all events
   */
  async getSubscriptionFullHistory(
    subscriptionId: string
  ): Promise<{
    success: boolean;
    events: any[];
    total: number;
  }> {
    const response = await api.get(`/api/billing-queue/items/${subscriptionId}/full-history`);
    return response.data;
  }

  /**
   * Remove a single medication from a subscription
   * The subscription remains active with the remaining medications
   */
  async removeMedicationFromSubscription(
    pharmacyId: string,
    subscriptionId: string,
    rxNumber: string,
    reason: string,
    notes?: string,
    // GP-1147: pins removal to the exact line item the user selected — duplicate
    // rows can share an rxNumber but differ in due date. The backend validates
    // the row at this index still carries rxNumber and 409s on a stale pin.
    medicationIndex?: number,
  ): Promise<{
    success: boolean;
    message: string;
    data: any;
  }> {
    const response = await api.patch(
      `/api/subscriptions/${pharmacyId}/${subscriptionId}/medications/${rxNumber}/remove`,
      { reason, notes, medicationIndex },
    );
    return response.data;
  }


  /**
   * Update a subscription's medications / dates / status.
   *
   * Single source of truth for the `PUT /api/subscriptions/:id` save that was
   * previously duplicated in SubscriptionHistory and SubscriptionManagementQueue
   * (both injected `subscriptionStatus: updates.status`). Idempotent PUT on a
   * known id — safe to retry. Errors are intentionally NOT swallowed here so the
   * caller (EditSubscriptionModal) can keep the dialog open with an inline error
   * + retry instead of optimistically closing on a failed save.
   */
  async updateSubscription(
    subscriptionId: string,
    updates: Record<string, any>,
  ): Promise<any> {
    const response = await api.put(`/api/subscriptions/${subscriptionId}`, {
      ...updates,
      // When updates.status is undefined, JSON serialization drops this key entirely (it never
      // reaches the backend as `subscriptionStatus: undefined`). Callers that only send
      // { medications } (e.g. adjust_dates) rely on this — no top-level date/status fields
      // reach the backend from those saves, only the per-medication changes.
      subscriptionStatus: updates.status,
    });
    return response.data;
  }

  // ============ WI-4: Drug-action Methods (add / dose-change) ============

  /**
   * Get a patient's ACTIVE subscriptions (Home + IV).
   *
   * Reuses the lightweight `GET /api/subscriptions/:pharmacyId/patient/:patientId/active`
   * endpoint (the same `findAllExistingSubscriptions` path SubscriptionTypeDialog
   * relies on via `/initialize`). Returns the canonical `{ home, iv, allHome, allIv }`
   * shape — callers typically flatten `[...allHome, ...allIv]`.
   */
  async getActiveSubscriptionsForPatient(
    pharmacyId: string,
    patientId: string,
    // GP-1104: the edit launcher opens on paused plans too (to offer Resume).
    // Active-only would resolve a paused whole-plan to zero subs and dead-end
    // on "No active subscription".
    includePaused = false,
  ): Promise<{
    home: BillingItem | null;
    iv: BillingItem | null;
    allHome: BillingItem[];
    allIv: BillingItem[];
  }> {
    const response = await api.get(
      `/api/subscriptions/${pharmacyId}/patient/${patientId}/active`,
      { params: includePaused ? { includePaused: true } : undefined },
    );
    return response.data;
  }

  /**
   * Add one or more medications to an existing subscription with manual due
   * dates (WI-4 Action B). Creates NO order — each drug joins the recurring
   * cycle and recomputes nextFollowUpDate. Backend rejects past dates and
   * duplicate active Rx numbers with a 400 naming the offending Rx; the error
   * is intentionally NOT swallowed so the dialog can surface the message.
   */
  async addMedicationsToSubscription(
    pharmacyId: string,
    id: string,
    body: {
      medications: Array<{
        rxNumber: string;
        nextDueDate: string; // YYYY-MM-DD (today or future)
        daysSupply?: number;
        quantity?: number;
        medicationName?: string;
        din?: string;
        /** Marks this drug as a new start (GP-1053) — drives new_start_home_del/new_start_iv at first spawn. */
        isNewStart?: boolean;
      }>;
      reason?: string;
      /**
       * Rx numbers this same save is about to discontinue (medication-change
       * saves adds before removes) — exempts them from the backend's
       * same-drug duplicate guard.
       */
      replacesRxNumbers?: string[];
    },
  ): Promise<{
    success: boolean;
    recalculatedFollowUpDate: string;
    addedMedications: any[];
  }> {
    const response = await api.patch(
      `/api/subscriptions/${pharmacyId}/${id}/medications`,
      body,
    );
    return response.data;
  }

  /**
   * Replace a subscription medication with a new strength/dose (WI-4 Action C).
   * By default (`createOrderNow: false`) the change is staged for the next
   * refill cycle. A `warning` in the response (e.g. an in-flight order) should
   * be surfaced to the user, not swallowed.
   */
  async changeMedicationDose(
    pharmacyId: string,
    id: string,
    oldRxNumber: string,
    body: {
      newRxNumber: string;
      newDaysSupply?: number;
      quantity?: number;
      // Required: a dose change must name the new drug (backend DoseChangeDto
      // enforces @IsNotEmpty). Keeping it required here catches any future
      // caller that omits it at compile time rather than via a runtime 400.
      medicationName: string;
      din?: string;
      strength?: string;
      reason?: string;
      createOrderNow?: boolean;
      /** Explicit YYYY-MM-DD due date for the new dose; absent ⇒ inherit the old drug's cadence. */
      nextDueDate?: string;
    },
  ): Promise<{
    success: boolean;
    recalculatedFollowUpDate: string;
    oldMedication: any;
    newMedication: any;
    warning?: string;
  }> {
    const response = await api.patch(
      `/api/subscriptions/${pharmacyId}/${id}/medications/${oldRxNumber}/dose-change`,
      body,
    );
    return response.data;
  }
  // ============ PRX-17: Align due dates ============

  /**
   * What aligning this subscription would do: the offered dates, the moves,
   * the skips with their reasons, any refusal, and the planKey to apply with.
   * Never writes.
   */
  async previewAlignDueDates(
    pharmacyId: string,
    basketId: string,
    body: AlignDueDatesPreviewBody,
  ): Promise<AlignDueDatesPreview> {
    const response = await api.post(
      `/api/subscriptions/${pharmacyId}/${basketId}/align-due-dates/preview`,
      body,
    );
    return response.data;
  }

  /**
   * Align the dates exactly as previewed. The server answers 409 when the
   * subscription changed since the preview (planKey) and 400 with a sentence
   * when the plan is refused.
   */
  async alignDueDates(
    pharmacyId: string,
    basketId: string,
    body: AlignDueDatesBody,
  ): Promise<AlignDueDatesResult> {
    const response = await api.post(
      `/api/subscriptions/${pharmacyId}/${basketId}/align-due-dates`,
      body,
    );
    return response.data;
  }
}

/*
 * PRX-17 — the align routes' shapes, mirroring
 * backend-v2/src/subscriptions/utils/align-plan.util.ts and
 * subscription-align.service.ts. Dates are plain YYYY-MM-DD strings.
 */
export type AlignSkipReason =
  | 'in_flight'
  | 'held_for_pack_change'
  | 'paused'
  | 'prn'
  | 'troubleshooting'
  | 'already_on_target'
  | 'not_selected'
  | 'invalid_due_date';

export interface AlignTargetOption {
  date: string | null;
  available: boolean;
  why?: string;
  overdueSince?: string;
}

export interface AlignMove {
  rxNumber: string;
  medicationName: string;
  from: string | null;
  to: string;
  cutShortDays: number;
}

export interface AlignPackMove {
  from: string | null;
  to: string;
  drugs: Array<{ rxNumber: string; medicationName: string }>;
  otherBasketCount: number;
}

export interface AlignSkip {
  rxNumber: string;
  medicationName: string;
  why: AlignSkipReason;
  returnsOn?: string;
  inPack?: true;
}

export interface AlignDueDatesPreview {
  basketId: string;
  target: string | null;
  targets: {
    earliest: AlignTargetOption;
    latest: AlignTargetOption;
    nextPackDate: AlignTargetOption | null;
  };
  moves: AlignMove[];
  packMove: AlignPackMove | null;
  skips: AlignSkip[];
  /**
   * `suggestion` comes only with a weekend or holiday refusal: the next business
   * day at THIS pharmacy, offered as a button, never applied by itself.
   */
  refusal: { code: string; message: string; suggestion?: string } | null;
  planKey: string;
  /** Moved Rx numbers with a twin line that cannot move; Save stays disabled. */
  duplicateRx: string[];
  /** The patient's pack-date source on a pack basket ('fillware' | 'manual'), else null. */
  blisterSource: string | null;
  /** Every loose drug that could move, once per Rx: what the per-drug picker offers. */
  looseDrugs: Array<{ rxNumber: string; medicationName: string; nextDueDate: string | null }>;
}

export interface AlignDueDatesPreviewBody {
  target?: string;
  rxNumbers?: string[];
  includePack?: boolean;
}

export interface AlignDueDatesBody {
  target: string;
  rxNumbers?: string[];
  includePack?: boolean;
  reason: string;
  planKey: string;
}

export interface AlignDueDatesResult {
  basketId: string;
  target: string;
  moved: AlignMove[];
  packMoved: { from: string | null; to: string } | null;
  skipped: AlignSkip[];
  nextFollowUpDate: string | null;
}

export const subscriptionService = new SubscriptionService();
